import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getHeaders } from './NCRHelpers';

const dashboardInitialState = {
  totalNcrs: 0,
  openNcrs: 0,
  closedNcrs: 0,
  inAction: 0,
  inReview: 0,
  inClosure: 0,
  actionAgingCount: 0,
  reviewAgingCount: 0,
  monthlyWiseNcrs: {},
  monthlyWiseCOPQ: {},
  locationWiseNcrs: {},
  categoryWiseNcrs: {},
  containmentActionWiseNcrs: {},
  rootCauseWiseNcrs: [],
  repeatedItemsWiseNcrs: {
    ncrsMorethantfive: { count: 0, ncrs: [] },
    ncrs_1X: { count: 0, ncrs: [] },
    ncrs_2X: { count: 0, ncrs: [] },
    ncrs_3X: { count: 0, ncrs: [] },
    ncrs_4X: { count: 0, ncrs: [] },
    ncrs_5X: { count: 0, ncrs: [] },
  }
};

// Async thunk for fetching financial years
export const fetchFinancialYears = createAsyncThunk(
  'dashboard/fetchFinancialYears',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/dashboard/ncr-financial-years`,
        getHeaders()
      );

      if (!response?.data?.data) {
        throw new Error('Invalid response structure from financial years API');
      }

      const data = Array.isArray(response.data.data) ? response.data.data : [];
      
      if (data.length === 0) {
        console.warn('No financial years available');
        return { financialYears: [], latestYear: null };
      }

      const latestYear = data[data.length - 1];

      if (!latestYear?.financialYear || !latestYear?.startYear || !latestYear?.endYear) {
        throw new Error('Invalid financial year data structure');
      }

      return {
        financialYears: data.sort((a, b) => (a.financialYear < b.financialYear ? 1 : -1)),
        latestYear
      };
    } catch (error) {
      console.error('Error fetching financial years:', error.message || error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching dashboard data
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchDashboardData',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      if (!startDate || !endDate) {
        console.warn('Start date or end date is missing');
        return dashboardInitialState;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/dashboard/get-dashboard-data?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        getHeaders()
      );

      if (!response?.data?.dashboardDetails) {
        throw new Error('Invalid response structure from dashboard data API');
      }

      return response.data.dashboardDetails;
    } catch (error) {
      console.error('Error fetching dashboard data:', error.message || error);
      return rejectWithValue(error.message);
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    data: dashboardInitialState,
    selectedFinancialYear: {
      financialYear: '',
      startDate: '',
      endDate: '',
    },
    locationHoverIndex: null,
    categoryHoverIndex: null,
    financialYears: [],
    hoveredCardIndex: null,
    isCheckCOPQ: false,
    isTogggleDashboard: false,
    loading: false,
    error: null,
  },
  reducers: {
    setSelectedFinancialYear: (state, action) => {
      state.selectedFinancialYear = action.payload;
    },
    setLocationHoverIndex: (state, action) => {
      state.locationHoverIndex = action.payload;
    },
    setCategoryHoverIndex: (state, action) => {
      state.categoryHoverIndex = action.payload;
    },
    setHoveredCardIndex: (state, action) => {
      state.hoveredCardIndex = action.payload;
    },
    setIsCheckCOPQ: (state, action) => {
      state.isCheckCOPQ = action.payload;
    },
    setIsTogggleDashboard: (state, action) => {
      state.isTogggleDashboard = action.payload;
    },
    resetDashboardData: (state) => {
      state.data = dashboardInitialState;
      state.isTogggleDashboard = false;
      state.selectedFinancialYear = {
        financialYear: '',
        startDate: '',
        endDate: '',
      };
      state.financialYears = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Financial Years
      .addCase(fetchFinancialYears.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinancialYears.fulfilled, (state, action) => {
        state.loading = false;
        state.financialYears = action.payload.financialYears;
        
        if (action.payload.latestYear) {
          state.selectedFinancialYear = {
            financialYear: action.payload.latestYear.financialYear,
            startDate: action.payload.latestYear.startYear,
            endDate: action.payload.latestYear.endYear,
          };
        }
      })
      .addCase(fetchFinancialYears.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.financialYears = [];
      })
      // Fetch Dashboard Data
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = dashboardInitialState;
      });
  },
});

export const {
  setSelectedFinancialYear,
  setLocationHoverIndex,
  setCategoryHoverIndex,
  setHoveredCardIndex,
  setIsCheckCOPQ,
  setIsTogggleDashboard,
  resetDashboardData,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
